import React, { useMemo, useState, useRef, useEffect } from "react";

/**
 * FvgHeavyTradesChart
 *
 * Pure input-driven chart: pass your own `fvgs` and `heavyTrades` arrays as props
 * and the component derives everything (axis extremes, ticks, positions) from them.
 * No sample/default data is baked in.
 *
 * Renders:
 *  - fvgs: a vertical line per entry from candleMinus3.low -> candleMinus1.high (Y),
 *          positioned at creationDate (X)
 *  - heavyTrades: a circle per entry at Y = currentPrice * multiplier,
 *                 positioned at creationDate (X)
 *
 * Axes are labelled (time on X, price on Y) with extremes derived from the data.
 *
 * Usage:
 *   <FvgHeavyTradesChart fvgs={myFvgs} heavyTrades={myHeavyTrades} />
 *
 * Props:
 *   fvgs         Array<{ candleMinus1: {high, low}, candleMinus3: {high, low}, creationDate }>
 *   heavyTrades  Array<{ creationDate, currentPrice, multiplier }>
 *   width        number (optional, default 900)
 *   height       number (optional, default 460)
 */

//RSI_CROSSED_SMA_BELOW_BBMIDDLE

//BELOW_BBLOWER

//HEAVY-ABSORPTION

const MARGIN = { top: 24, right: 32, bottom: 56, left: 64 };

function formatTime(ts) {
  if (!Number.isFinite(ts)) return "";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString([], {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function formatPrice(v, numberOfDecimalValues) {
  return Number(v).toFixed(numberOfDecimalValues);
}

// Derive a visual circle radius from a trade multiplier.
// Uses a nonlinear transform (square-root) so large multipliers don't produce excessive radii.
// Returns a numeric pixel radius, clamped between minR and maxR.
function radiusFromMultiplier(m, minR = 2, maxR = 28) {
  const radius = Number(m) / 10;
  //   if (!Number.isFinite(n) || n <= 0) return minR;
  //   // use sqrt scaling and a small multiplier to keep radii reasonable
  //   const r = Math.round(Math.sqrt(n) * 4);
  //   return Math.max(minR, Math.min(maxR, r));
  if (radius < 1) return 1
  return radius
}

function niceTicks(min, max, count = 5) {
  // guard against invalid inputs
  if (!Number.isFinite(min) || !Number.isFinite(max)) return [];
  if (min === max) {
    // if identical, expand a tiny amount to produce visible ticks
    const pad = Math.abs(min) * 0.01 || 1;
    min -= pad;
    max += pad;
  }
  const step = (max - min) / (count - 1);
  return Array.from({ length: count }, (_, i) => min + step * i);
}

// add time-aligned tick generator (15 minutes default)
function timeTicks(min, max, intervalMs = 15 * 60 * 1000) {
  if (!Number.isFinite(min) || !Number.isFinite(max) || intervalMs <= 0) return [];
  // align start to interval grid
  const start = Math.floor(min / intervalMs) * intervalMs;
  const ticks = [];
  const maxTicks = 200; // safety cap
  for (let t = start; t <= max && ticks.length < maxTicks; t += intervalMs) {
    // only include ticks within [min, max]
    if (t >= min && t <= max) ticks.push(t);
  }
  // ensure at least endpoints if nothing fell inside
  if (ticks.length === 0) return [min, max];
  return ticks;
}

export default function Chart({ 
  fvgs, 
  heavyTrades, 
  currentPrices, 
  rsiCrossedSmaBelowBbMiddle,
  belowBBLower,
  heavyAbsorption,
  heavySelling,
  // breakoutPhaseStarted,
  // artificialPumpStarted,
  // spoofingLayeringDetected,
  absorptionViaStreams,
  dollarValue,
  width = 1100, height = 660 }) {
  //  console.log(JSON.stringify(fvgs.slice(-2)))

  //  console.log(JSON.stringify(heavyTrades.slice(-5)))

  //take out numberOfDecimalValues from latest fvg
  const numberOfDecimalValues = fvgs[fvgs.length - 1]?.numberOfDecimalValues || 8;

  const [hovered, setHovered] = useState(null);

  const safeFvgs = Array.isArray(fvgs) ? fvgs : [];

  const safeDollarValues = Array.isArray(dollarValue) ? dollarValue : [];

  const safeHeavyTrades = Array.isArray(heavyTrades) ? heavyTrades : [];

  const safeCurrentPrices = Array.isArray(currentPrices) ? currentPrices : [];

  // const safeBreakoutPhaseStarted = Array.isArray(breakoutPhaseStarted) ? breakoutPhaseStarted : [];
  // const safeArtificialPumpStarted = Array.isArray(artificialPumpStarted) ? artificialPumpStarted : [];
  // const safeSpoofingLayeringDetected = Array.isArray(spoofingLayeringDetected) ? spoofingLayeringDetected : [];
  const safeAbsorptionViaStreams = Array.isArray(absorptionViaStreams) ? absorptionViaStreams : [];

  const safeRSICrossedSmaBelowBbMiddle = Array.isArray(rsiCrossedSmaBelowBbMiddle) ? rsiCrossedSmaBelowBbMiddle : [];
  const safeBelowBBLower = Array.isArray(belowBBLower) ? belowBBLower : [];
  const safeHeavyAbsorption = Array.isArray(heavyAbsorption) ? heavyAbsorption : [];
  const safeHeavySelling = Array.isArray(heavySelling) ? heavySelling : [];

  const innerWidth = width - MARGIN.left - MARGIN.right;
  const innerHeight = height - MARGIN.top - MARGIN.bottom;

  // Pan/scroll view state
  const [viewX, setViewX] = useState({ min: NaN, max: NaN });
  const [viewY, setViewY] = useState({ min: NaN, max: NaN }); // <-- new for Y-axis zooming
  const panRef = useRef({ isPanning: false, startX: 0, startView: null });

  const svgRef = useRef(null); // attach to SVG for mouse-position based zoom

  const belowBBLowerPoints = useMemo(
   () =>
     safeBelowBBLower.map((t) => ({
       ...t,
       yValue: Number(t.currentPrice),
     })),
   [safeBelowBBLower]
 );

 const heavyAbsorptionPoints = useMemo(
   () =>
     safeHeavyAbsorption.map((t) => ({
       ...t,
       yValue: Number(t.currentPrice),
     })),
   [safeHeavyAbsorption]
 );

 const heavySellingPoints = useMemo(
   () =>
     safeHeavySelling.map((t) => ({
       ...t,
       yValue: Number(t.currentPrice),
     })),
   [safeHeavySelling]
 );

 const rsiCrossedSmaBelowBBMiddlePoints = useMemo(
   () =>
     safeRSICrossedSmaBelowBbMiddle.map((t) => ({
       ...t,
       yValue: Number(t.currentPrice),
     })),
   [safeRSICrossedSmaBelowBbMiddle]
 );

  const heavyPoints = useMemo(
    () =>
      safeHeavyTrades.map((t) => ({
        ...t,
        yValue: Number(t.currentPrice),
      })),
    [safeHeavyTrades]
  );
  
  const currentPoints = useMemo(() =>
    (safeCurrentPrices || []).map(p => ({
      ...p,
      yValue: Number(p.currentPrice),
    })),
    [safeCurrentPrices]
  );

  // const breakoutPhaseStartedPoints = useMemo(
  //   () =>
  //    (safeBreakoutPhaseStarted || []).map(p => ({
  //     ...p,
  //     yValue: Number(p.currentPrice),
  //   })),
  //   [safeBreakoutPhaseStarted]
  // );

  // const artificialPumpStartedPoints = useMemo(
  //   () =>
  //     (safeArtificialPumpStarted || []).map((t) => ({
  //       ...t,
  //       yValue: Number(t.currentPrice),
  //     })),
  //   [safeArtificialPumpStarted]
  // );

  // const spoofingLayeringDetectedPoints = useMemo(
  //   () =>
  //     (safeSpoofingLayeringDetected || []).map((t) => ({
  //       ...t,
  //       yValue: Number(t.currentPrice),
  //     })),
  //   [safeSpoofingLayeringDetected]
  // );

  const absorptionViaStreamsPoints = useMemo(
    () =>
      (safeAbsorptionViaStreams || []).map((t) => ({
        ...t,
        yValue: Number(t.currentPrice),
      })),
    [safeAbsorptionViaStreams]
  );

  const { xMin, xMax, yMin, yMax } = useMemo(() => {
    const xs = [
      ...safeFvgs.map((f) => f.creationDate),
      ...heavyPoints.map((t) => t.creationDate),
      ...currentPoints.map((p) => p.creationDate),
      ...rsiCrossedSmaBelowBBMiddlePoints.map((r) => r.creationDate),
      ...belowBBLowerPoints.map((b) => b.creationDate),
      ...heavyAbsorptionPoints.map((h) => h.creationDate),
      ...heavySellingPoints.map((s) => s.creationDate),
      // ...breakoutPhaseStartedPoints.map((b) => b.creationDate),
      // ...artificialPumpStartedPoints.map((a) => a.creationDate),
      // ...spoofingLayeringDetectedPoints.map((s) => s.creationDate),
      ...absorptionViaStreamsPoints.map((a) => a.creationDate),
    ];
    const ys = [
      ...safeFvgs.flatMap((f) => [f?.candleMinus1.high, f?.candleMinus3.low]),
      ...heavyPoints.map((t) => t.yValue),
      ...currentPoints.map((p) => p.yValue),
      ...rsiCrossedSmaBelowBBMiddlePoints.map((r) => r.yValue),
      ...belowBBLowerPoints.map((b) => b.yValue),
      ...heavyAbsorptionPoints.map((h) => h.yValue),
      ...heavySellingPoints.map((s) => s.yValue),
      // ...breakoutPhaseStartedPoints.map((b) => b.yValue),
      // ...artificialPumpStartedPoints.map((a) => a.yValue),
      // ...spoofingLayeringDetectedPoints.map((s) => s.yValue),
      ...absorptionViaStreamsPoints.map((a) => a.yValue),
    ];

    let xMin = Math.min(...xs);
    let xMax = Math.max(...xs);
    let yMin = Math.min(...ys);
    let yMax = Math.max(...ys);

    // padding so points/lines aren't flush against the edges
    const xPad = (xMax - xMin) * 0.08 || 60000; // fallback: 1 minute
    const yPad = (yMax - yMin) * 0.12 || 1;

    return {
      xMin: xMin - xPad,
      xMax: xMax + xPad,
      yMin: yMin - yPad,
      yMax: yMax + yPad,
    };
  }, [safeFvgs, 
    heavyPoints, 
    belowBBLowerPoints, 
    rsiCrossedSmaBelowBBMiddlePoints, 
    heavyAbsorptionPoints, 
    heavySellingPoints,
    // breakoutPhaseStartedPoints, 
    // artificialPumpStartedPoints, 
    // spoofingLayeringDetectedPoints,
    absorptionViaStreamsPoints, 
    currentPoints
  ]);

  // active view window (allows horizontal pan/scroll)
  const activeXMin = Number.isFinite(viewX.min) ? viewX.min : xMin;
  const activeXMax = Number.isFinite(viewX.max) ? viewX.max : xMax;
  const activeYMin = Number.isFinite(viewY.min) ? viewY.min : yMin; // <-- use viewY when zoomed
  const activeYMax = Number.isFinite(viewY.max) ? viewY.max : yMax;

  const xScale = (ts) => {
    if (!Number.isFinite(ts)) return MARGIN.left;
    const min = activeXMin;
    const max = activeXMax;
    if (!Number.isFinite(min) || !Number.isFinite(max) || min === max) {
      return MARGIN.left + innerWidth / 2;
    }
    return MARGIN.left + ((ts - min) / (max - min)) * innerWidth;
  };

  const yScale = (price) =>
    MARGIN.top + innerHeight - ((price - activeYMin) / (activeYMax - activeYMin)) * innerHeight; // <-- uses activeY

  const xTicks = useMemo(() => {
    const min = Number.isFinite(activeXMin) ? activeXMin : NaN;
    const max = Number.isFinite(activeXMax) ? activeXMax : NaN;
    if (!Number.isFinite(min) || !Number.isFinite(max)) return [];
      return niceTicks(min, max, 6);
   // return timeTicks(min, max, 15 * 60 * 1000);
  }, [activeXMin, activeXMax]);

  const yTicks = useMemo(() => {
    if (!Number.isFinite(activeYMin) || !Number.isFinite(activeYMax)) return [];
    return niceTicks(activeYMin, activeYMax, 6);
  }, [activeYMin, activeYMax]);

  // initialize view window to full data range when first data arrives
  useEffect(() => {
    if (Number.isFinite(xMin) && Number.isFinite(xMax)) {
      setViewX(prev => {
        if (Number.isFinite(prev.min) && Number.isFinite(prev.max)) return prev;
        return { min: xMin, max: xMax };
      });
    }
    if (Number.isFinite(yMin) && Number.isFinite(yMax)) {
      setViewY(prev => {
        if (Number.isFinite(prev.min) && Number.isFinite(prev.max)) return prev;
        return { min: yMin, max: yMax };
      });
    }
  }, [xMin, xMax, yMin, yMax]);

  // Pan handlers
  const onMouseDown = (e) => {
    panRef.current.isPanning = true;
    panRef.current.startX = e.clientX;
    panRef.current.startView = { min: activeXMin, max: activeXMax };
    document.body.style.cursor = 'grabbing';
  };
  const onMouseMove = (e) => {
    if (!panRef.current.isPanning) return;
    const dx = e.clientX - panRef.current.startX; // pixels
    const span = panRef.current.startView.max - panRef.current.startView.min;
    if (!span) return;
    const timeDelta = (dx / innerWidth) * span;
    let newMin = panRef.current.startView.min - timeDelta;
    let newMax = panRef.current.startView.max - timeDelta;
    if (newMin < xMin) { newMin = xMin; newMax = xMin + span; }
    if (newMax > xMax) { newMax = xMax; newMin = xMax - span; }
    setViewX({ min: newMin, max: newMax });
  };
  const endPan = () => {
    panRef.current.isPanning = false;
    panRef.current.startView = null;
    document.body.style.cursor = '';
  };

  const onWheel = (e) => {
    // guard before preventDefault
    if (e.cancelable) e.preventDefault();

    const delta = e.deltaY || e.deltaX;

    // Zoom X when Ctrl/Cmd is pressed
    if (e.ctrlKey || e.metaKey) {
      if (!Number.isFinite(activeXMin) || !Number.isFinite(activeXMax)) return;
      const span = activeXMax - activeXMin;
      if (!span) return;
      const scale = Math.exp(delta * 0.001); // >1 => zoom out, <1 => zoom in
      const rect = svgRef.current?.getBoundingClientRect();
      const clientX = rect ? (e.clientX - rect.left) : (MARGIN.left + innerWidth / 2);
      const ratio = (clientX - MARGIN.left) / innerWidth;
      const center = activeXMin + ratio * span;
      let newMin = center - (center - activeXMin) * scale;
      let newMax = center + (activeXMax - center) * scale;
      // clamp to data bounds
      if (newMin < xMin) { newMin = xMin; }
      if (newMax > xMax) { newMax = xMax; }
      setViewX({ min: newMin, max: newMax });
      return;
    }

    // Zoom Y when Alt is pressed
    if (e.altKey) {
      if (!Number.isFinite(activeYMin) || !Number.isFinite(activeYMax)) return;
      const span = activeYMax - activeYMin;
      if (!span) return;
      const scale = Math.exp(delta * 0.001);
      const rect = svgRef.current?.getBoundingClientRect();
      const clientY = rect ? (e.clientY - rect.top) : (MARGIN.top + innerHeight / 2);
      const rel = (clientY - MARGIN.top) / innerHeight; // 0..1 from top
      // convert to price (top -> max)
      const center = activeYMax - rel * span;
      let newMin = center - (center - activeYMin) * scale;
      let newMax = center + (activeYMax - center) * scale;
      // clamp
      if (newMin < yMin) newMin = yMin;
      if (newMax > yMax) newMax = yMax;
      setViewY({ min: newMin, max: newMax });
      return;
    }

    // Default: vertical wheel pans horizontally (existing behavior)
    if (!Number.isFinite(activeXMin) || !Number.isFinite(activeXMax)) return;
    const span = activeXMax - activeXMin;
    const timeDelta = (delta / innerWidth) * span; // map pixels -> time
    let newMin = activeXMin + timeDelta;
    let newMax = activeXMax + timeDelta;
    if (newMin < xMin) { newMin = xMin; newMax = xMin + span; }
    if (newMax > xMax) { newMax = xMax; newMin = xMax - span; }
    setViewX({ min: newMin, max: newMax });
  };

  return (
    <div style={{ fontFamily: "system-ui, sans-serif", color: "#1f2937" }}>
      <svg
        ref={svgRef}
        width={width}
        height={height}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={endPan}
        onMouseLeave={endPan}
        onWheel={onWheel}
        style={{ background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: 8, cursor: panRef.current.isPanning ? 'grabbing' : 'grab' }}
      >
        {/* Y gridlines + labels */}
        {yTicks.map((v, i) => {
          const y = yScale(v);
          return (
            <g key={`y-${i}`}>
              <line
                x1={MARGIN.left}
                x2={width - MARGIN.right}
                y1={y}
                y2={y}
                stroke="#f0f1f3"
                strokeWidth={1}
              />
              <text
                x={MARGIN.left - 10}
                y={y}
                textAnchor="end"
                dominantBaseline="middle"
                fontSize={11}
                fill="#6b7280"
              >
                {formatPrice(v, numberOfDecimalValues)}
              </text>
            </g>
          );
        })}

        {/* X gridlines + labels */}
        {xTicks.map((t, i) => {
          const x = xScale(t);
          return (
            <g key={`x-${i}`}>
              <line
                x1={x}
                x2={x}
                y1={MARGIN.top}
                y2={height - MARGIN.bottom}
                stroke="#f0f1f3"
                strokeWidth={1}
              />
              <text
                x={x}
                y={height - MARGIN.bottom + 18}
                textAnchor="middle"
                fontSize={11}
                fill="#6b7280"
              >
                {formatTime(t)}
              </text>
            </g>
          );
        })}

        {/* Axes */}
        <line
          x1={MARGIN.left}
          x2={width - MARGIN.right}
          y1={height - MARGIN.bottom}
          y2={height - MARGIN.bottom}
          stroke="#9ca3af"
          strokeWidth={1.5}
        />
        <line
          x1={MARGIN.left}
          x2={MARGIN.left}
          y1={MARGIN.top}
          y2={height - MARGIN.bottom}
          stroke="#9ca3af"
          strokeWidth={1.5}
        />

        {/* Axis titles */}
        <text
          x={MARGIN.left + innerWidth / 2}
          y={height - 8}
          textAnchor="middle"
          fontSize={12}
          fontWeight={600}
          fill="#374151"
        >
          Time
        </text>
        <text
          x={16}
          y={MARGIN.top + innerHeight / 2}
          textAnchor="middle"
          fontSize={12}
          fontWeight={600}
          fill="#374151"
          transform={`rotate(-90, 16, ${MARGIN.top + innerHeight / 2})`}
        >
          Price
        </text>

        {/* FVG vertical lines */}
        {/* {safeFvgs.map((f, i) => {
          if (f.candleMinus1 && f.candleMinus2 && f.candleMinus3) {
            const FOUR_HOURS = 4 * 60 * 60 * 1000;
            let x3 = xScale(f.candleMinus3.closeTime - FOUR_HOURS);
            let x2 = xScale(f.candleMinus2.closeTime - FOUR_HOURS);
            let x1 = xScale(f.candleMinus1.closeTime - FOUR_HOURS);
            let endTime = xScale(f.candleMinus1.closeTime);
          
            const candleMinus3yTop = yScale(f.candleMinus3.high);
            const candleMinus3yBottom = yScale(f.candleMinus3.low);

            const candleMinus2yTop = yScale(f.candleMinus2.high);
            const candleMinus2yBottom = yScale(f.candleMinus2.low);

            const candleMinus1yTop = yScale(f.candleMinus1.high);
            const candleMinus1yBottom = yScale(f.candleMinus1.low);

            return (
              <g
                key={`fvg-${i}`}
                style={{ cursor: "pointer", }}

              >
                <line //candleMinus3
                  x1={x3}
                  x2={x3}
                  y1={candleMinus3yTop}
                  y2={candleMinus3yBottom}
                  stroke={f.candleMinus3.close > f.candleMinus3.open ? "green" : "red"}
                  strokeWidth={hovered?.index == i && hovered.type == "fvg" ? 10 : 7} //should be as thick as 4 hours on the graph
                  opacity={hovered?.index == i && hovered.type == "fvg" ? .8 : .4}
                  onMouseEnter={() => setHovered({ type: "fvg", index: i })}
                  onMouseLeave={() => setHovered(null)}
                />
                <line //candleMinus2
                  x1={x2}
                  x2={x2}
                  y1={candleMinus2yTop}
                  y2={candleMinus2yBottom}
                  stroke={f.candleMinus2.close > f.candleMinus2.open ? "green" : "red"}
                  strokeWidth={hovered?.index == i && hovered.type == "fvg" ? 10 : 7} //should be as thick as 4 hours on the graph
                  opacity={hovered?.index == i && hovered.type == "fvg" ? .8 : .4}
                  onMouseEnter={() => setHovered({ type: "fvg", index: i })}
                  onMouseLeave={() => setHovered(null)}
                />
                <line //candleMinus1
                  x1={x1}
                  x2={x1}
                  y1={candleMinus1yTop}
                  y2={candleMinus1yBottom}
                  stroke={f.candleMinus1.close > f.candleMinus1.open ? "green" : "red"}
                  strokeWidth={hovered?.index == i && hovered.type == "fvg" ? 10 : 7} //should be as thick as 4 hours on the graph
                  opacity={hovered?.index == i && hovered.type == "fvg" ? .8 : .4}
                  onMouseEnter={() => setHovered({ type: "fvg", index: i })}
                  onMouseLeave={() => setHovered(null)}
                />
              </g>
            );
          }
        })} */}

        

        {/* Heavy trade circles */}
        {heavyPoints.map((t, i) => {
          const x = xScale(t.creationDate);
          const y = yScale(t.yValue);
          return (
            <g
              key={`trade-${i}`}
              style={{ cursor: "pointer" }}
            >
              <circle
                cx={x}
                cy={y}
                r={radiusFromMultiplier(t.multiplier)}
                fill={t.side == "BULLISH" ? "green" : "red"}
                fillOpacity={hovered?.index == i && hovered.type == "trade" ? 0.8 : .3}
                stroke={t.side == "BULLISH" ? "green" : "red"}
                strokeWidth={0}
                onMouseEnter={() => setHovered({ type: "trade", index: i })}
                onMouseLeave={() => setHovered(null)}
              />
            </g>
          );
        })}


        {rsiCrossedSmaBelowBBMiddlePoints.map((t, i) => {
          const x = xScale(t.creationDate);
          const y = yScale(t.yValue);
          return (
            <g
              key={`rsi-${i}`}
              style={{ cursor: "pointer" }}
            >
              <circle
                cx={x}
                cy={y}
                r={4}
                fill="blue"
                fillOpacity={.4}
                stroke={"blue"}
                strokeWidth={0}
                onMouseEnter={() => setHovered({ type: "rsiCrossedSmaBelowBBMiddlePoint", index: i })}
                onMouseLeave={() => setHovered(null)}
              />
            </g>
          );
        })}

        {/* {belowBBLowerPoints.map((t, i) => {
          const x = xScale(t.creationDate);
          const y = yScale(t.yValue);
          return (
            <g
              key={`belowBBLowerPoints-${i}`}
              style={{ cursor: "pointer" }}
            >
              <circle
                cx={x}
                cy={y}
                r={6}
                fill="brown"
                fillOpacity={.8}
                stroke={"brown"}
                strokeWidth={0}
                onMouseEnter={() => setHovered({ type: "belowBBLowerPoint", index: i })}
                onMouseLeave={() => setHovered(null)}
              />
            </g>
          );
        })} */}
       
        {heavyAbsorptionPoints.map((t, i) => {
          const x = xScale(t.creationDate);
          const y = yScale(t.yValue);
          return (
            <g
              key={`heavyAbsorptionPoints-${i}`}
              style={{ cursor: "pointer" }}
            >
              <circle
                cx={x}
                cy={y}
                r={8}
                fill="yellow"
                fillOpacity={.8}
                stroke={"green"}
                strokeWidth={2}
                onMouseEnter={() => setHovered({ type: "heavyAbsorptionPoint", index: i })}
                onMouseLeave={() => setHovered(null)}
              />
            </g>
          );
        })}

         {heavySellingPoints.map((t, i) => {
          const x = xScale(t.creationDate);
          const y = yScale(t.yValue);
          return (
            <g
              key={`heavySellingPoints-${i}`}
              style={{ cursor: "pointer" }}
            >
              <circle
                cx={x}
                cy={y}
                r={8}
                fill="yellow"
                fillOpacity={.8}
                stroke={"red"}
                strokeWidth={2}
                onMouseEnter={() => setHovered({ type: "heavySellingPoint", index: i })}
                onMouseLeave={() => setHovered(null)}
              />
            </g>
          );
        })}



        {/* Current price small black dots (radius 1) */}
        {currentPoints.map((p, i) => {
          const x = xScale(p.creationDate);
          const y = yScale(p.yValue);
          return (
            <g key={`current-${i}`} style={{ cursor: "pointer" }}>
              <circle
                cx={x}
                cy={y}
                r={1}
                fill="#000"
                opacity={.3}
                onMouseEnter={() => setHovered({ type: "currentPrice", index: i })}
                onMouseLeave={() => setHovered(null)}
              />
            </g>
          );
        })}

        {/* {breakoutPhaseStartedPoints.map((t, i) => {
          const x = xScale(t.creationDate);
          const y = yScale(t.yValue);
          return (
            <g key={`breakout-${i}`} style={{ cursor: "pointer" }}>
              <text x={x} y={y} textAnchor="middle" fontSize={13} fill="#fc9d03ff"
                 onMouseEnter={() => setHovered({ type: "breakoutPhaseStarted", index: i })}
                onMouseLeave={() => setHovered(null)}
              >
               B
              </text>
            </g>
          );
        })} */}

        {/* {artificialPumpStartedPoints.map((t, i) => {
          const x = xScale(t.creationDate);
          const y = yScale(t.yValue);
          return (
            <g key={`artificial-${i}`} style={{ cursor: "pointer" }}>
               <text x={x} y={y} textAnchor="middle" fontSize={13} fill="#e600a8ff" 
                  onMouseEnter={() => setHovered({ type: "artificialPumpStarted", index: i })}
                onMouseLeave={() => setHovered(null)}
               >
               P
              </text>
            </g>
          );
        })} */}

        {/* {spoofingLayeringDetectedPoints.map((t, i) => {
          const x = xScale(t.creationDate);
          const y = yScale(t.yValue);
          return (
            <g key={`spoofing-${i}`} style={{ cursor: "pointer" }}>
               <text x={x} y={y} textAnchor="middle" fontSize={13} fill="#0b03fc"
                  onMouseEnter={() => setHovered({ type: "spoofingLayeringDetected", index: i })}
                onMouseLeave={() => setHovered(null)}
               >
               S
              </text>
            </g>
          );
        })} */}

        {absorptionViaStreamsPoints.map((t, i) => {
          const x = xScale(t.creationDate);
          const y = yScale(t.yValue);
           const size = 4;
          const pts = t.side == "BULLISH"
            ? `${x - size},${y + size} ${x + size},${y + size} ${x},${y - size * 0.2}` //up
            : `${x - size},${y - size} ${x + size},${y - size} ${x},${y + size * 0.2}`; //down

          return (
            <g key={`absorption-${i}`} style={{ cursor: "pointer" }}>
             
              <polygon points={pts} fill={t.side == "BULLISH" ? "green" : "red"}
               onMouseEnter={() => setHovered({ type: "absorptionViaStreams", index: i })}
                onMouseLeave={() => setHovered(null)}
              />

            </g>
          );
        })}

        {/* Dollar Value Triangles */}
        {safeDollarValues.map((t, i) => {
          const x = xScale(t.creationDate);
          const y = yScale(t.currentPrice);
           const size = 3;
          const arrowDirection = t.side == "BULLISH" ? "right" : "left";
          // build a horizontal triangle: pointing right -> tip at +x, pointing left -> tip at -x
          const arrowPoints = arrowDirection === "right"
            ? `${x - size},${y - size} ${x - size},${y + size} ${x + size},${y}`
            : `${x + size},${y - size} ${x + size},${y + size} ${x - size},${y}`;

          return (
            <g key={`dollarValue-${i}`} style={{ cursor: "pointer" }}>
              <polygon points={arrowPoints}
                fill={t.side == "BULLISH" ? "blue" : "orange"}
                onMouseEnter={() => setHovered({ type: "dollarValue", index: i })}
                onMouseLeave={() => setHovered(null)}
              />

            </g>
          );
        })}

        {/* Tooltip */}
        {hovered &&
          (() => {
            if (hovered.type === "dollarValue") {
              const f = safeDollarValues[hovered.index];
              const x = xScale(f.creationDate);
              const y = yScale(f.currentPrice);
              return (
                <g transform={`translate(${x}, ${y})`}>
                  <rect x={-75} y={-40} width={150} height={40} rx={6} fill={f.side == "BULLISH" ? "blue" : "orange"} opacity={0.9} />
                   <text x={0} y={-24} textAnchor="middle" fontSize={11} fill="#fff">
                     {formatTime(f.creationDate)}
                   </text>
                   <text x={0} y={-10} textAnchor="middle" fontSize={11} fill="#fff">
                    ${f?.dollarValueOfSymbol/1000}k ~ {formatPrice(f.currentPrice, numberOfDecimalValues)}
                  </text>
                </g>
              );
            }
            if (hovered.type === "trade") {
              const t = heavyPoints[hovered.index];
              const x = xScale(t.creationDate);
              const y = yScale(t.yValue) - 14;
              return (
                <g transform={`translate(${x}, ${y})`}>
                  <rect x={-75} y={-40} width={150} height={40} rx={6} fill={t.side == "BULLISH" ? "green" : "red"} opacity={0.9} />
                  <text x={0} y={-24} textAnchor="middle" fontSize={11} fill="#fff">
                    {formatTime(t.creationDate)}
                  </text>
                  <text x={0} y={-10} textAnchor="middle" fontSize={11} fill="#fff">
                    XTrade: {t.multiplier.toFixed(0)}X, {formatPrice(t.currentPrice, numberOfDecimalValues)}
                  </text>

                </g>
              );
            }
            if (hovered.type === "currentPrice") {
              const t = currentPoints[hovered.index];
              const x = xScale(t.creationDate);
              const y = yScale(t.yValue) - 14;
              return (
                <g transform={`translate(${x}, ${y})`}>
                  <rect x={-75} y={-40} width={150} height={40} rx={6} fill="#111827" opacity={0.9} />
                  <text x={0} y={-24} textAnchor="middle" fontSize={11} fill="#fff">
                    {formatTime(t.creationDate)}
                  </text>
                  <text x={0} y={-10} textAnchor="middle" fontSize={11} fill="#fff">
                    Current: {formatPrice(t.currentPrice, numberOfDecimalValues)}
                  </text>

                </g>
              );
            }
            if (hovered.type === "rsiCrossedSmaBelowBBMiddlePoint") {
              const t = rsiCrossedSmaBelowBBMiddlePoints[hovered.index];
              const x = xScale(t.creationDate);
              const y = yScale(t.yValue) - 14;
              return (
                <g transform={`translate(${x}, ${y})`}>
                  <rect x={-75} y={-40} width={150} height={40} rx={6} fill="#3a3a3aff" opacity={0.9} />
                  <text x={0} y={-24} textAnchor="middle" fontSize={11} fill="#fff">
                    {formatTime(t.creationDate)}
                  </text>
                  <text x={0} y={-10} textAnchor="middle" fontSize={11} fill="#fff">
                    RSI XB BBLower: {formatPrice(t.currentPrice, numberOfDecimalValues)}
                  </text>

                </g>
              );
            }
            if (hovered.type === "belowBBLowerPoint") {
              const t = belowBBLowerPoints[hovered.index];
              const x = xScale(t.creationDate);
              const y = yScale(t.yValue) - 14;
              return (
                <g transform={`translate(${x}, ${y})`}>
                  <rect x={-75} y={-40} width={150} height={40} rx={6} fill="#111827" opacity={0.9} />
                  <text x={0} y={-24} textAnchor="middle" fontSize={11} fill="#fff">
                    {formatTime(t.creationDate)}
                  </text>
                  <text x={0} y={-10} textAnchor="middle" fontSize={11} fill="#fff">
                   Below BBLower: {formatPrice(t.currentPrice, numberOfDecimalValues)}
                  </text>

                </g>
              );
            }
            if (hovered.type === "heavyAbsorptionPoint") {
              const t = heavyAbsorptionPoints[hovered.index];
              const x = xScale(t.creationDate);
              const y = yScale(t.yValue) - 14;
              return (
                <g transform={`translate(${x}, ${y})`}>
                  <rect x={-75} y={-40} width={150} height={40} rx={6} fill="#111827" opacity={0.9} />
                  <text x={0} y={-24} textAnchor="middle" fontSize={11} fill="#fff">
                    {formatTime(t.creationDate)}
                  </text>
                  <text x={0} y={-10} textAnchor="middle" fontSize={11} fill="#fff">
                    Absorption: {formatPrice(t.currentPrice, numberOfDecimalValues)}
                  </text>

                </g>
              );
            }
            //  if (hovered.type === "breakoutPhaseStarted") {
            //   const t = breakoutPhaseStartedPoints[hovered.index];
            //   const x = xScale(t.creationDate);
            //   const y = yScale(t.yValue) - 14;
            //   return (
            //     <g transform={`translate(${x}, ${y})`}>
            //       <rect x={-75} y={-40} width={150} height={40} rx={6} fill="#111827" opacity={0.9} />
            //       <text x={0} y={-24} textAnchor="middle" fontSize={11} fill="#fff">
            //         {formatTime(t.creationDate)}
            //       </text>
            //       <text x={0} y={-10} textAnchor="middle" fontSize={11} fill="#fff">
            //         Breakout: {formatPrice(t.currentPrice, numberOfDecimalValues)}
            //       </text>

            //     </g>
            //   );
            // }
            //  if (hovered.type === "artificialPumpStarted") {
            //   const t = artificialPumpStartedPoints[hovered.index];
            //   const x = xScale(t.creationDate);
            //   const y = yScale(t.yValue) - 14;
            //   return (
            //     <g transform={`translate(${x}, ${y})`}>
            //       <rect x={-75} y={-40} width={150} height={40} rx={6} fill="#111827" opacity={0.9} />
            //       <text x={0} y={-24} textAnchor="middle" fontSize={11} fill="#fff">
            //         {formatTime(t.creationDate)}
            //       </text>
            //       <text x={0} y={-10} textAnchor="middle" fontSize={11} fill="#fff">
            //         Arti. Pump: {formatPrice(t.currentPrice, numberOfDecimalValues)}
            //       </text>

            //     </g>
            //   );
            // }
            //  if (hovered.type === "spoofingLayeringDetected") {
            //   const t = spoofingLayeringDetectedPoints[hovered.index];
            //   const x = xScale(t.creationDate);
            //   const y = yScale(t.yValue) - 14;
            //   return (
            //     <g transform={`translate(${x}, ${y})`}>
            //       <rect x={-75} y={-40} width={150} height={40} rx={6} fill="#111827" opacity={0.9} />
            //       <text x={0} y={-24} textAnchor="middle" fontSize={11} fill="#fff">
            //         {formatTime(t.creationDate)}
            //       </text>
            //       <text x={0} y={-10} textAnchor="middle" fontSize={11} fill="#fff">
            //         Spoofing: {formatPrice(t.currentPrice, numberOfDecimalValues)}
            //       </text>

            //     </g>
            //   );
            // }
            if (hovered.type === "absorptionViaStreams") {
              const t = absorptionViaStreamsPoints[hovered.index];
              const x = xScale(t.creationDate);
              const y = yScale(t.yValue) - 14;
              return (
                <g transform={`translate(${x}, ${y})`}>
                  <rect x={-75} y={-40} width={150} height={40} rx={6} fill="#111827" opacity={0.9} />
                  <text x={0} y={-24} textAnchor="middle" fontSize={11} fill="#fff">
                    {formatTime(t.creationDate)}
                  </text>
                  <text x={0} y={-10} textAnchor="middle" fontSize={11} fill="#fff">
                    Abs. Streams: {formatPrice(t.currentPrice, numberOfDecimalValues)}
                  </text>

                </g>
              );
            }

            return null
          })()}

      </svg>

      {/* Legend */}
      <div style={{ display: "flex", gap: 20, marginTop: 10, fontSize: 13 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ width: 4, height: 15, background: "grey", display: "inline-block" }} />

          <span style={{ color : "white"}}>FVG</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span
            style={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              background: "grey",
              display: "inline-block",
       
            }}
          />
         <span style={{ color : "white"}}>Heavy Trade</span>
        </div>
         <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span
            style={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              background: "blue",
              display: "inline-block",
   
            }}
          />
          <span style={{ color : "white"}}>RSI</span>, <span style={{ color : "white"}}>Below BB Lower</span>, <span style={{ color : "white"}}>Heavy-Absorption</span>
        </div>
      </div>
    </div>
  );
}
